import { useEffect, useId, useState } from 'react';
import { monthlyChartDateBoundary, parisCalendarDate } from './remuneration-chart-date';
import type { FinancialOverview } from './financial-overview-api';
import { ForgeExchangeScene } from './ForgeExchangeScene';
import { PrototypeIcon } from './PrototypeIcon';
import { FinanceExplanation } from './FinanceExplanation';
import remunerationIcon from '../assets/finance/remuneration.svg?no-inline';
import annualIcon from '../assets/finance/remuneration-annual.svg?no-inline';
import settingsIcon from '../assets/finance/settings.svg?no-inline';
import { balanceForPeriod } from './financial-bank-balance';
import { FinancialAmount } from './FinancialCalculationStatus';
import { financialMoney } from './financial-money';
import type { FinancialCalculationKey } from './financial-calculation-roadmap';

export const treasurySignalNames = [
  'Alerte critique !',
  'Fragile',
  'Des économies à faire !',
  'Tout va bien. RAS.',
  'Tréso bénéficiaire',
  'Super rendement !',
] as const;
export const defaultSignalThresholds = [25, 50, 65, 80, 100] as const;
export function treasurySignal(
  ratio: number | null,
  thresholds: readonly number[] = defaultSignalThresholds,
): number | null {
  return ratio === null || !Number.isFinite(ratio)
    ? null
    : thresholds.filter((threshold) => ratio * 100 >= threshold).length + 1;
}
export const remunerationMoney = financialMoney;
const monthName = (period: string): string =>
  new Intl.DateTimeFormat('fr-FR', { month: 'long', timeZone: 'UTC' }).format(
    new Date(`${period}-01T12:00:00Z`),
  );
const money = remunerationMoney;
const monthLabels = [
  'Jan.',
  'Fév.',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juil.',
  'Août',
  'Sep.',
  'Octobre',
  'Nov.',
  'Déc.',
];
export function RemunerationLegend(): React.JSX.Element {
  return (
    <ul className="remuneration-legend">
      {[6, 4, 1, 5, 3].map((signal) => (
        <li className={`treasury-signal-${signal}`} key={signal}>
          <i />
          {treasurySignalNames[signal - 1]}
        </li>
      ))}
    </ul>
  );
}
type Month = NonNullable<FinancialOverview['remuneration']>['months'][number];

export function TreasuryGauge({
  ratio,
  thresholds,
}: {
  readonly ratio: number | null;
  readonly thresholds: readonly number[];
}): React.JSX.Element {
  const signal = treasurySignal(ratio, thresholds);
  return (
    <div
      className={`remuneration-gauge treasury-signal-${signal ?? 'unknown'}`}
      role="img"
      aria-label={`Réserve de trésorerie : ${signal === null ? 'non évaluée' : treasurySignalNames[signal - 1]}, ${ratio === null ? 'ratio inconnu' : `${Math.round(ratio * 100)} % de la cible`}`}
    >
      <span className="remuneration-gauge__label">Santé de trésorerie</span>
      <div className="remuneration-gauge__rail">
        <i
          style={{
            height: `${ratio === null ? 0 : Math.max(0, Math.min(100, (ratio / 1.2) * 100))}%`,
          }}
        />
        {[120, 100, ...thresholds, 10]
          .filter((n, index, all) => all.indexOf(n) === index)
          .map((n) => (
            <b key={n} style={{ bottom: `${n / 1.2}%` }} />
          ))}
      </div>
      <div className="remuneration-gauge__ticks" aria-hidden="true">
        {[
          120,
          100,
          ...thresholds
            .filter((n) => n !== 100)
            .slice()
            .reverse(),
          10,
        ]
          .filter((n, index, all) => all.indexOf(n) === index)
          .sort((a, b) => b - a)
          .map((n) => (
            <span key={n} style={{ bottom: `${n / 1.2}%` }}>
              {n}%
            </span>
          ))}
      </div>
    </div>
  );
}

function HistoryChart({
  rows,
  annual = false,
  thresholds,
  reserve,
  onPeriod,
  onScroll,
}: {
  readonly rows: readonly {
    label: string;
    period?: string | undefined;
    cents: string | null;
    forecast?: boolean;
    bankCents?: string | null | undefined;
    bankDate?: string | null | undefined;
    retainedCents?: string | null | undefined;
    detail: string;
  }[];
  readonly annual?: boolean;
  readonly thresholds: readonly number[];
  readonly reserve: string | null;
  readonly onPeriod?: ((period: string) => void) | undefined;
  readonly onScroll?: ((left: number) => void) | undefined;
}): React.JSX.Element {
  const id = useId();
  const maximum = Math.max(1, ...rows.map((row) => Number(row.cents ?? 0)));
  const width = 360;
  const column = (width - 20) / rows.length;
  const x = (index: number): number => 10 + column * (index + 0.5);
  const y = (cents: string): number => 190 - (Number(cents) / maximum) * 140;
  // La courbe est distincte des barres mais suit exactement leurs variations,
  // sans réduire chaque mois à une même ligne horizontale.
  const signalY = (cents: string): number => Math.max(13, y(cents) - 42);
  const shortBar = (cents: string | null): boolean => cents !== null && 200 - y(cents) < 70;
  const activate = (period?: string): void => {
    if (period) onPeriod?.(period);
  };
  return (
    <>
      <div
        className="remuneration-chart-scroll"
        onScroll={(event) => onScroll?.(event.currentTarget.scrollLeft)}
        role="region"
        aria-label={
          annual
            ? 'Graphique des rémunérations annuelles, défilement horizontal possible'
            : 'Graphique des rémunérations mensuelles, défilement horizontal possible'
        }
        tabIndex={0}
      >
        <svg
          className="remuneration-chart"
          viewBox={annual ? '0 35 360 220' : '0 0 360 255'}
          role="group"
          aria-labelledby={id}
        >
          <title id={id}>
            {annual
              ? 'Moyenne mensuelle de la rémunération chaque année'
              : 'Rémunération prise chaque mois'}
            .{' '}
            {annual
              ? 'Gris : flux nul ou inconnu, ou prévision à objectif constant.'
              : 'Gris : prévisions à objectif constant ou données absentes.'}
          </title>
          <g className="remuneration-chart__grid" aria-hidden="true">
            {[50, 80, 110, 140, 170].map((line) => (
              <line key={line} x1="0" y1={line} x2="360" y2={line} />
            ))}
          </g>
          {rows.map((row, index) => {
            const next = rows[index + 1];
            const ratio =
              !annual && row.bankCents != null && reserve !== null && BigInt(reserve) > 0n
                ? Number(row.bankCents) / Number(reserve)
                : null;
            // La couleur annuelle ne compare plus un ancien solde à la réserve actuelle.
            // Elle lit seulement le signe du flux net annuel déjà fourni par le domaine.
            // Aucun seuil de « rendement » n'est inventé ; prévisions et inconnus restent gris.
            const signal = annual
              ? row.forecast || row.retainedCents == null || BigInt(row.retainedCents) === 0n
                ? null
                : BigInt(row.retainedCents) > 0n
                  ? 5
                  : 1
              : treasurySignal(ratio, thresholds);
            return (
              <g
                key={`${row.label}-${index}`}
                className={`${row.forecast ? 'remuneration-chart__forecast' : ''} treasury-signal-${signal ?? 'unknown'}`}
              >
                {!annual &&
                row.cents !== null &&
                next?.cents != null &&
                !next.forecast &&
                !row.forecast ? (
                  <line
                    className="remuneration-chart__line"
                    x1={x(index)}
                    y1={signalY(row.cents)}
                    x2={x(index + 1)}
                    y2={signalY(next.cents)}
                  />
                ) : null}
                <g
                  role={row.period && onPeriod ? 'button' : undefined}
                  tabIndex={row.period && onPeriod ? 0 : undefined}
                  aria-label={`${row.label} : ${money(row.cents)}. ${row.detail}`}
                  onClick={() => activate(row.period)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      activate(row.period);
                    }
                  }}
                >
                  <title>
                    {row.label} : {money(row.cents)} · {row.detail}
                    {row.bankDate ? ` · solde Freebe du ${row.bankDate}` : ''}
                  </title>
                  <rect
                    className="remuneration-chart__bar"
                    x={x(index) - Math.min(11, column * 0.34)}
                    y={row.cents === null ? 180 : y(row.cents)}
                    width={Math.min(22, column * 0.68)}
                    height={row.cents === null ? 10 : Math.max(10, 200 - y(row.cents))}
                    rx="11"
                  />
                  <text
                    className={`remuneration-chart__value${!annual && shortBar(row.cents) ? ' remuneration-chart__value--outside' : ''}`}
                    transform={`translate(${x(index) + (shortBar(row.cents) && !annual ? 9 : 3)},${!annual && shortBar(row.cents) && row.cents !== null ? y(row.cents) - 9 : 193}) rotate(-90)`}
                  >
                    {row.cents === null ? '—' : money(row.cents)}
                  </text>
                  {!annual && row.cents !== null && !row.forecast ? (
                    <circle
                      className="remuneration-chart__dot"
                      cx={x(index)}
                      cy={signalY(row.cents)}
                      r="5"
                    />
                  ) : null}
                  <text
                    className="remuneration-chart__label"
                    transform={`translate(${x(index) + 3},210) rotate(-90)`}
                    textAnchor="end"
                  >
                    {row.label}
                  </text>
                </g>
              </g>
            );
          })}
        </svg>
      </div>
      {annual ? (
        <ul className="remuneration-legend" aria-label="Flux annuel de trésorerie">
          <li className="treasury-signal-5">
            <i />
            Flux positif
          </li>
          <li className="treasury-signal-1">
            <i />
            Flux négatif
          </li>
          <li className="treasury-signal-unknown">
            <i />
            Nul, inconnu ou prévision
          </li>
        </ul>
      ) : (
        <RemunerationLegend />
      )}
    </>
  );
}

export function RemunerationColumn({
  overview,
  onGoal,
  onPeriod,
}: {
  readonly overview: FinancialOverview;
  readonly onGoal: () => void;
  readonly onPeriod?: ((period: string) => void) | undefined;
}): React.JSX.Element {
  const [calendarDate, setCalendarDate] = useState(() => parisCalendarDate(new Date()));
  const [monthlyScrollLeft, setMonthlyScrollLeft] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => setCalendarDate(parisCalendarDate(new Date())), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  const projection = overview.remuneration;
  const year = Number(overview.period.slice(0, 4));
  const month = Number(overview.period.slice(5));
  const thresholds = overview.signalThresholds ?? defaultSignalThresholds;
  const goal = overview.salaryGoalCents;
  const current = projection?.months.find((row) => row.period === overview.period);
  const currentAnnual = projection?.years.find((row) => row.year === year);
  const bank = balanceForPeriod(overview, overview.period).cents;
  const reserve = overview.reserveTargetCents;
  const ratio =
    bank !== null && reserve !== null && BigInt(reserve) > 0n
      ? Number(bank) / Number(reserve)
      : null;
  const previous = projection?.months.find(
    (row) => row.period === new Date(Date.UTC(year, month - 2, 1)).toISOString().slice(0, 7),
  );
  const previousBank = previous ? balanceForPeriod(overview, previous.period).cents : null;
  const after = (additional: string | null | undefined): number | null =>
    ratio === null || additional == null || bank === null || reserve === null
      ? null
      : Number(BigInt(bank) - BigInt(additional)) / Number(reserve);
  const taken =
    current === undefined ? overview.coverage.directorRemunerationCents : current.takenCents;
  const values = [
    { label: 'Objectif mensuel', cents: goal, ratio: null },
    {
      label: 'Conseillé ce mois-ci',
      cents: projection?.advisedCents ?? null,
      ratio: after(projection?.additionalCents),
    },
    { label: 'Finalement pris', cents: taken, ratio },
    {
      label: 'Trésorerie conservée si conseil suivi',
      cents:
        projection?.retainedCents != null && projection.additionalCents != null
          ? (BigInt(projection.retainedCents) - BigInt(projection.additionalCents)).toString()
          : null,
      ratio,
    },
    {
      label: `Rémunération du mois précédent${previous ? ` (${monthName(previous.period)})` : ''}`,
      cents: previous?.takenCents ?? null,
      ratio:
        previousBank !== null && reserve !== null && BigInt(reserve) > 0n
          ? Number(previousBank) / Number(reserve)
          : null,
    },
    {
      label: 'Objectif annuel selon l’objectif actuel',
      cents: goal === null ? null : (BigInt(goal) * BigInt(year === 2020 ? 4 : 12)).toString(),
      ratio: null,
    },
    {
      label: 'Réellement pris sur l’année',
      cents: currentAnnual?.totalCents ?? null,
      ratio,
    },
  ];
  const monthlyRows = Array.from({ length: year === 2020 ? 4 : 12 }, (_, index) => {
    const period = `${year}-${String(index + (year === 2020 ? 9 : 1)).padStart(2, '0')}`;
    const row: Month | undefined = projection?.months.find((entry) => entry.period === period);
    // Le mois sélectionné n'est pas la date courante. Octobre reste du réalisé
    // lorsque l'on consulte septembre ; seuls les mois hors historique sont futurs.
    const forecast = period > (overview.observedThroughPeriod ?? overview.period);
    const balance = balanceForPeriod(overview, period);
    return {
      label: monthLabels[index + (year === 2020 ? 8 : 0)]!,
      period: forecast ? undefined : period,
      cents: forecast ? goal : (row?.takenCents ?? null),
      bankCents: balance.cents,
      bankDate: balance.date,
      forecast,
      detail: forecast
        ? 'Prévision : objectif mensuel constant'
        : row?.takenCents == null
          ? 'Rémunération non déterminée'
          : `Opérations Freebe importées${period === overview.period && overview.provisional ? ', mois en cours' : ''}`,
    };
  });
  const monthlyBoundary = monthlyChartDateBoundary(year, calendarDate);
  const referenceYear = Number((overview.observedThroughPeriod ?? overview.period).slice(0, 4));
  const annualRows = Array.from({ length: referenceYear - 2019 + 5 }, (_, index) => {
    const rowYear = index + 2020;
    const row = projection?.years.find((entry) => entry.year === rowYear);
    const forecast = rowYear > referenceYear;
    return {
      label: String(rowYear),
      cents: forecast ? goal : (row?.averageCents ?? null),
      retainedCents: row?.retainedCents ?? null,
      forecast,
      detail: forecast
        ? 'Prévision : objectif mensuel constant'
        : `${row?.knownMonths ?? 0}/${row?.activeMonths ?? (rowYear === 2020 ? 4 : rowYear === year ? month : 12)} mois connus${rowYear === year ? ', année en cours' : ''}. Flux de trésorerie conservé sur l’année, prélèvements déjà déduits : ${money(row?.retainedCents)}.`,
    };
  });
  return (
    <section className="finance-column remuneration-column" aria-label="Ma rémunération">
      <h2 className="finance-column__title">
        <span aria-hidden="true">“</span>Combien je me paie ?
      </h2>
      <article
        className="remuneration-card remuneration-card--current finance-card--controls"
        aria-label={`Rémunération : ${monthName(overview.period)} ${year}`}
      >
        <div className="remuneration-card__heading finance-card__heading">
          <h3 className="finance-card__title">
            <img className="finance-card-icon" src={remunerationIcon} alt="" />
            Rémunération
          </h3>
          <div className="finance-card__controls">
            <select
              className="remuneration-card__month"
              aria-label="Mois de rémunération"
              value={overview.period}
              onChange={(event) => onPeriod?.(event.currentTarget.value)}
            >
              {Array.from(
                { length: 12 },
                (_, index) => `${year}-${String(index + 1).padStart(2, '0')}`,
              )
                .filter(
                  (period) =>
                    period >= '2020-09' &&
                    period <=
                      new Date().toLocaleDateString('en-CA', {
                        timeZone: 'Europe/Paris',
                        year: 'numeric',
                        month: '2-digit',
                      }),
                )
                .map((period) => (
                  <option key={period} value={period}>
                    {monthName(period)}
                  </option>
                ))}
            </select>
            <button
              type="button"
              aria-label="Configurer l’objectif de rémunération"
              onClick={onGoal}
            >
              <img src={settingsIcon} alt="" />
            </button>
          </div>
        </div>
        <dl>
          {values.map((value) => (
            <div
              className={
                value.label === 'Objectif mensuel' || value.label === 'Finalement pris'
                  ? 'remuneration-card__prominent'
                  : undefined
              }
              key={value.label}
            >
              <dt>
                {value.label}
                {value.label === 'Réellement pris sur l’année' ? (
                  <em> ({currentAnnual?.knownMonths ?? 0} mois / 12)</em>
                ) : null}
              </dt>
              <dd
                className={`treasury-signal-${
                  value.label === 'Trésorerie conservée si conseil suivi' && value.cents != null
                    ? BigInt(value.cents) < 0n
                      ? 1
                      : 5
                    : (treasurySignal(value.ratio, thresholds) ?? 'unknown')
                }`}
                title={
                  value.label === 'Trésorerie conservée si conseil suivi' && value.cents != null
                    ? BigInt(value.cents) < 0n
                      ? 'Flux de trésorerie négatif après le versement simulé'
                      : 'Flux de trésorerie positif ou nul après le versement simulé'
                    : value.ratio === null
                      ? 'Impact sur la trésorerie non évalué'
                      : treasurySignalNames[treasurySignal(value.ratio, thresholds)! - 1]
                }
              >
                {value.label === 'Trésorerie conservée si conseil suivi' &&
                value.cents != null &&
                BigInt(value.cents) > 0n
                  ? '+'
                  : ''}
                <FinancialAmount
                  cents={value.cents}
                  hint={
                    value.label === 'Conseillé ce mois-ci'
                      ? (projection?.adviceBlocker ?? undefined)
                      : value.label === 'Trésorerie conservée si conseil suivi'
                        ? projection?.retainedCents == null
                          ? 'Flux du mois incomplets'
                          : (projection?.adviceBlocker ?? undefined)
                        : undefined
                  }
                  reason={
                    value.label === 'Conseillé ce mois-ci' ||
                    value.label === 'Trésorerie conservée si conseil suivi'
                      ? projection?.adviceReason
                      : undefined
                  }
                  calculation={
                    (
                      {
                        'Conseillé ce mois-ci': 'remunerationAdvice',
                        'Trésorerie conservée si conseil suivi': 'retainedCash',
                        'Objectif mensuel': 'settings',
                        'Objectif annuel selon l’objectif actuel': 'settings',
                      } as Record<string, FinancialCalculationKey>
                    )[value.label] ?? 'sources'
                  }
                />
              </dd>
            </div>
          ))}
        </dl>
        {projection?.additionalCents === '0' ? (
          <p className="remuneration-card__advice-note">
            Aucun complément
            {goal != null && taken != null && BigInt(taken) >= BigInt(goal)
              ? ' · objectif déjà atteint'
              : ' selon les données disponibles'}
            .
          </p>
        ) : projection?.advisedCents != null && overview.obligations?.complete === false ? (
          <p className="remuneration-card__advice-note">Conseil estimé · provisions chiffrées.</p>
        ) : null}
        {projection?.advisedCents == null && projection?.adviceReason ? (
          <FinanceExplanation title="Pourquoi ce conseil est indisponible ?">
            <p>{projection.adviceReason}</p>
          </FinanceExplanation>
        ) : null}
        <div className="remuneration-card__scene">
          <TreasuryGauge ratio={ratio} thresholds={thresholds} />
          <ForgeExchangeScene />
        </div>
      </article>
      <article
        className="remuneration-card remuneration-card--monthly finance-card--controls"
        style={{
          // Mobile charts retain their 360 px minimum and can scroll. Use the
          // same width and scroll offset to keep the card boundary on the bar.
          backgroundImage: `linear-gradient(to right, #ef3e20 ${monthlyBoundary === 0 || monthlyBoundary === 1 ? `${monthlyBoundary * 100}%` : `calc(max(100% - var(--remuneration-card-padding) * 2, 360px) * ${monthlyBoundary} + var(--remuneration-card-padding) - ${monthlyScrollLeft}px)`}, #000 0)`,
        }}
      >
        <div className="remuneration-card__heading finance-card__heading">
          <h3 className="finance-card__title">
            <PrototypeIcon name="chart-column" />
            Rémunération 1 mois après l’autre
          </h3>
          <div className="finance-card__controls">
            <label className="remuneration-card__year">
              <span className="finance-visually-hidden">Année des rémunérations</span>
              <select
                value={year}
                onChange={(event) =>
                  onPeriod?.(`${event.currentTarget.value}-${overview.period.slice(5)}`)
                }
              >
                {Array.from(
                  { length: Math.max(year, new Date().getFullYear()) - 2019 },
                  (_, index) => (
                    <option key={index} value={2020 + index}>
                      {2020 + index}
                    </option>
                  ),
                )}
              </select>
            </label>
          </div>
        </div>
        <HistoryChart
          rows={monthlyRows}
          onScroll={setMonthlyScrollLeft}
          thresholds={thresholds}
          reserve={reserve}
          onPeriod={onPeriod}
        />
        <FinanceExplanation>
          <p>
            Barres : rémunérations prises en {year}. Les mois à venir reprennent votre objectif.
          </p>
          <p>
            Les pastilles comparent le solde daté du mois à la réserve de sécurité. Il ne s’agit pas
            nécessairement du solde après rémunération. Sans solde daté, la pastille reste grise.
          </p>
        </FinanceExplanation>
      </article>
      <article className="remuneration-card remuneration-card--annual">
        <h3>
          <img className="finance-card-icon" src={annualIcon} alt="" />
          Rémunération 1 année après l’autre
        </h3>
        <HistoryChart rows={annualRows} annual thresholds={thresholds} reserve={reserve} />
        <FinanceExplanation>
          <p>
            Barres : rémunération mensuelle moyenne des mois connus. Les années futures reprennent
            l’objectif actuel, sans prévision d’encaissements.
          </p>
          <p>
            Couleurs : signe du flux annuel connu, entrées moins sorties, rémunération déjà déduite.
            Vert : positif ; rouge : négatif ; gris : nul, incomplet ou futur. Ce flux n’est ni un
            bénéfice ni un solde bancaire.
          </p>
        </FinanceExplanation>
      </article>
    </section>
  );
}
